import React, { useState, useRef, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Paper,
  Avatar,
  Divider,
  Button,
  Badge,
  List,
  ListItem,
  ListItemText,
  Link as MuiLink,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import WebIcon from "@mui/icons-material/Web";
import AdminPanelSettings from "@mui/icons-material/AdminPanelSettings";
import DeveloperMode from "@mui/icons-material/DeveloperMode";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ReactMarkdown from "react-markdown";
import sendRequest from "../utils/sendRequest";
import { AUTH_ENDPOINT, LOGIN_ENDPOINT, NOTIFICATION_ENDPOINT } from "../utils/constants";

const drawerWidth = 240;

const ICON_MAP = {
  lux_admin: <AdminPanelSettings />,
  lux_dev: <DeveloperMode />,
};

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const toTitleCase = (str) =>
  str
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

const AppTrayMenu = () => {
  const [apps, setApps] = useState([]);
  const [userData, setUserData] = useState({ name: "", email: "" });
  const [isAppsOpen, setIsAppsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const appsMenuRef = useRef(null);
  const profileMenuRef = useRef(null);
  const notificationsMenuRef = useRef(null);

  const socketRef = useRef(null);
  const isConnectedRef = useRef(false);

  const connectWebSocket = () => {
    const wsUrl = `${NOTIFICATION_ENDPOINT}/ws`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      isConnectedRef.current = true;
      ws.send(JSON.stringify({ action: "subscribe", payload: {} }));
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleWebSocketMessage(message);
    };

    ws.onclose = () => {
      isConnectedRef.current = false;
      setTimeout(() => {
        if (!isConnectedRef.current) {
          connectWebSocket();
        }
      }, 5000);
    };

    ws.onerror = (error) => console.error("WebSocket error:", error);
    socketRef.current = ws;
  };

  const handleWebSocketMessage = (message) => {
    switch (message.action) {
      case "subscribed":
        break;
      case "recent_notifications":
        if (Array.isArray(message.payload)) {
          setNotifications(message.payload);
          const unread = message.payload.filter((n) => !n.read).length;
          setUnreadCount(unread);
        }
        break;
      case "new_notification":
        if (message.payload) {
          setNotifications((prev) => [message.payload, ...prev]);
          if (!message.payload.read) {
            setUnreadCount((prev) => prev + 1);
          }
        }
        break;
      case "notifications_updated":
        if (Array.isArray(message.payload)) {
          setNotifications((prev) =>
            prev.map((notif) => {
              const updated = message.payload.find((n) => n.id === notif.id);
              return updated ? updated : notif;
            })
          );
          setNotifications((currentNotifs) => {
            const unread = currentNotifs.filter((n) => !n.read).length;
            setUnreadCount(unread);
            return currentNotifs;
          });
        }
        break;
      case "pong":
        break;
      default:
        console.log("Unhandled WebSocket message:", message);
    }
  };

  const markAsRead = (notificationIds) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN || !notificationIds.length) return;

    socket.send(JSON.stringify({ action: "mark_read", payload: { notification_ids: notificationIds } }));
  };

  const markAllAsRead = () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length > 0) markAsRead(unreadIds);
  };

  const handleLogout = () => {
    sendRequest(`${AUTH_ENDPOINT}/logout`, null, null, "POST", false, false)
      .then(() => {
        window.location.href = LOGIN_ENDPOINT;
      })
      .catch((err) => console.error("Logout error:", err));
  };

  const getInitials = (name) =>
    name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const handleNotificationClick = (n) => {
    if (!n.read) markAsRead([n.id]);
    if (n.url) window.open(n.url, "_blank");
  };

  const formatNotificationTimestamp = (timestamp) => {
    const d = new Date(timestamp);
    const now = new Date();
    const isToday =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();

    return isToday
      ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric" })
      : d.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
        });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const data = await sendRequest(`${AUTH_ENDPOINT}/user_meta`);
        setApps(data.apps);
        setUserData({ name: data.name, email: data.email });
        connectWebSocket();
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserData();

    return () => {
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!appsMenuRef.current?.contains(e.target)) setIsAppsOpen(false);
      if (!profileMenuRef.current?.contains(e.target)) setIsProfileOpen(false);
      if (!notificationsMenuRef.current?.contains(e.target)) setIsNotificationsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box ref={appsMenuRef} sx={{ position: "relative" }}>
        <IconButton color="inherit" onClick={() => setIsAppsOpen(!isAppsOpen)} size="medium" sx={{ mr: 1 }}>
          <AppsIcon />
        </IconButton>
        {isAppsOpen && (
          <Paper elevation={4} sx={{ position: "absolute", right: 0, top: "55px", width: "320px", p: 2, borderRadius: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
              {apps.filter((app) => app.url).map((app) => (
                <Box
                  key={app.short_name}
                  component="a"
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "flex", flexDirection: "column", alignItems: "center", textDecoration: "none" }}
                >
                  <Box sx={{ width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center", mb: 1, color: "primary.main" }}>
                    {app.icon ? (
                      <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor" dangerouslySetInnerHTML={{ __html: app.icon }} />
                    ) : ICON_MAP[app.short_name] ? (
                      React.cloneElement(ICON_MAP[app.short_name], { sx: { fontSize: 32 } })
                    ) : (
                      <WebIcon sx={{ fontSize: 32 }} />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ textAlign: "center", lineHeight: 1.2 }}>{app.name}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}
      </Box>

      <Box ref={notificationsMenuRef} sx={{ position: "relative" }}>
        <IconButton
          color="inherit"
          onClick={() => {
            setIsNotificationsOpen(!isNotificationsOpen);
            if (!isNotificationsOpen && unreadCount > 0) {
              const visibleIds = notifications.filter((n) => !n.read).slice(0, 10).map((n) => n.id);
              if (visibleIds.length) markAsRead(visibleIds);
            }
          }}
          size="medium"
          sx={{ mr: 1 }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        {isNotificationsOpen && (
          <Paper elevation={4} sx={{ position: "absolute", right: 0, top: "55px", width: "350px", maxHeight: "500px", overflowY: "auto", borderRadius: 2 }}>
            <Box sx={{ p: 2, display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6">Notifications</Typography>
              {unreadCount > 0 && (
                <Button size="small" onClick={markAllAsRead}>Mark all as read</Button>
              )}
            </Box>
            <Divider />
            <List>
              {notifications.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography color="text.secondary">No notifications</Typography>
                </Box>
              ) : (
                notifications.map((n) => (
                  <ListItem
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    sx={{
                      py: 1,
                      px: 2,
                      borderLeft: !n.read ? "4px solid" : "none",
                      borderLeftColor: "primary.main",
                      backgroundColor: !n.read ? "action.hover" : "inherit",
                      "&:hover": { backgroundColor: "action.hover", cursor: "pointer" },
                    }}
                    divider
                  >
                    <ListItemText
                      primary={
                        <Typography variant="caption" color="text.secondary">
                          {toTitleCase(n.app_id.split("_")[1])} • {formatNotificationTimestamp(n.created_at)}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body1" sx={{ fontWeight: "medium", color: "text.primary", mt: 0.5 }}>
                            {n.notification_title || n.title}
                          </Typography>
                          <Box sx={{ fontSize: "0.875rem", color: "text.secondary", mt: 0.5 }}>
                            <ReactMarkdown disallowedElements={["h1"]}>
                              {n.notification_body || n.text}
                            </ReactMarkdown>
                          </Box>
                        </>
                      }
                      disableTypography
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        )}
      </Box>

      <Box ref={profileMenuRef} sx={{ position: "relative" }}>
        <IconButton onClick={() => setIsProfileOpen(!isProfileOpen)} size="medium">
          <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>{getInitials(userData.name)}</Avatar>
        </IconButton>
        {isProfileOpen && (
          <Paper elevation={4} sx={{ position: "absolute", right: 0, top: "55px", width: "280px", borderRadius: 2, p: 2 }}>
            <Box sx={{ mb: 2, textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56, fontSize: "1.5rem", margin: "0 auto 1rem" }}>
                {getInitials(userData.name)}
              </Avatar>
              <Typography variant="subtitle1">{userData.name}</Typography>
              <Typography variant="body2" color="text.secondary">{userData.email}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button endIcon={<LogoutIcon />} onClick={handleLogout} color="error">Logout</Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

const LuxAppBar = () => (
  <StyledAppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
    <Toolbar sx={{ minHeight: "48px" }}>
      <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, cursor: "pointer" }}>
        <MuiLink component={RouterLink} to="/purchase-orders" underline="hover" sx={{ ml: 1, color: "inherit" }}>
          Lux Internal - PO Dashboard
        </MuiLink>
      </Typography>
      <AppTrayMenu />
    </Toolbar>
  </StyledAppBar>
);

export default LuxAppBar;
